import { Row } from "antd"
import {
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Icon,
  Number,
  Skeleton,
  Tag,
  Text,
  Title,
  Tooltip,
} from "components/common"
import { InfoBlock } from "components/custom"
import ThemeConfig from "configs/theme"
import { getAppLanguage } from "configs/useInitializeApp"
import { DATE_FORMAT, TIME_FORMAT } from "constants"
import { CLIENT_LISTING_PATH } from "constants/app-paths"
import { useAddClientContactViewTrackingMutation, useMarkFavoriteUnFavoriteMutation } from "features/clients/detail/api"
import { t } from "i18next"
import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { convertToReadableString, joinBytItems, localisedValue, openMailTo } from "utils"
import { dateTimeFunction } from "utils/date"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { ACTION_TYPES, PAGE_SUBSECTIONS } from "utils/gtm/gtmEventsData"
import getPrivilege from "utils/privileges/privileges"
import { configs } from "../../../../configs"
import ClientForm from "../clientForm/clientForm"
import WhatsAppChat from "../whatsAppChat/whatsAppChat"
import styles from "./clientDetailInfo.module.less"
import ClientContactCard from "./components/clientContactCard/clientContactCard"
import ClientSalesDetail from "./components/clientSalesDetail"
import { categoryItem } from "features/shared/data"

function ClientDetailInfo({ skeletonLoading, data }) {
  const { t } = useTranslation()
  const language = getAppLanguage()
  const [markFavoriteUnFavorite, { data: favoriteData, isFetching: isFetchingFavorite, error }] =
    useMarkFavoriteUnFavoriteMutation()
  const [addClientContactView] = useAddClientContactViewTrackingMutation()
  const clientFormRef = useRef()
  const navigate = useNavigate()
  const whatsAppChatRef = useRef()
  const { state } = useLocation()
  const userPrivileges = useSelector(state => state.app.privileges)
  const countryName = useSelector(state => state?.app?.user?.info?.AgencyConfiguration?.Country?.name)

  const { vasSales = [], transactionalSales = [] } = data?.activeSales || {}
  const vasTransactionSales = vasSales?.length && transactionalSales?.length
  const [selectedSales, setSelectedSales] = useState(() =>
    vasTransactionSales ? "vas" : vasSales?.length ? "vas" : transactionalSales?.length ? "transactional" : null
  )

  const effectiveType = useMemo(() => {
    if (selectedSales) return selectedSales
    if (vasSales?.length && transactionalSales?.length) return "vas"
    if (vasSales?.length) return "vas"
    if (transactionalSales?.length) return "transactional"
    return null
  }, [selectedSales, vasSales, transactionalSales])

  const salesData = useMemo(() => {
    if (effectiveType === "vas") return vasSales
    if (effectiveType === "transactional") return transactionalSales
    return []
  }, [effectiveType, vasSales, transactionalSales])

  const demographicData = useMemo(() => {
    const finalDemographicData = data?.demographicProfile?.data
    if (!finalDemographicData || !Array.isArray(finalDemographicData)) return finalDemographicData
    return finalDemographicData.map(item =>
      item?.key === "marketExposure"
        ? { ...item, label: t(`Has exposure to ${countryName || ""} market?`, { country: countryName }) }
        : item
    )
  }, [data?.demographicProfile?.data, countryName, t])

  const onClickFavorite = async () => {
    const resp = await markFavoriteUnFavorite({
      clientIds: [data?.id],
      favorite: data?.ClientFavorite?.length === 0,
    })
      .unwrap()
      .catch(e => {
        fireEvent({ ...ACTION_TYPES.mark_client_as_favorite, response: e.status, client_id: data?.id })
      })

    if (!error) {
      data?.ClientFavorite?.length === 0 &&
        fireEvent({ ...ACTION_TYPES.mark_client_as_favorite, response: "200", client_id: data?.id })
    }
  }

  return (
    <Card className={styles.clientSidebar}>
      {skeletonLoading ? (
        <ClientDetailInfoSkeleton />
      ) : (
        <>
          <Group template="initial" gap="32px">
            <Group gap="16px" className={styles.clientInfoSticky}>
              <Row align="middle" style={{ gap: "4px 8px", marginBlock: -8 }}>
                <Button
                  type="muted"
                  shape="circle"
                  icon={language?.rtl ? "HiArrowRight" : "HiArrowLeft"}
                  onClick={() => (state?.hasPath ? navigate(-1) : navigate(CLIENT_LISTING_PATH))}
                />
                <Text type="secondary" style={{ fontSize: "85%" }}>
                  ID:&nbsp;{data?.id}
                </Text>
                <div style={{ flexGrow: 1 }}>
                  {data?.ClientStage?.name && (
                    <Tag size="small" color={data?.ClientStage?.style?.bgColor}>
                      {data?.ClientStage?.name}
                    </Tag>
                  )}
                  {data?.referralType && (
                    <Tag size="small" color="#919699">
                      {data?.referralType == "direct" ? t("Direct") : t("Affiliate")}
                    </Tag>
                  )}
                </div>
                {getPrivilege("can_edit_client", data?.privileges) && (
                  <Button
                    className="p-0"
                    type="link"
                    icon="TbEdit"
                    iconProps={{ size: "1.1em" }}
                    text={t("Edit")}
                    size="small"
                    style={{ gap: 2 }}
                    onClick={() => {
                      fireEvent({ ...ACTION_TYPES.client_edit_button, client_id: data?.id })
                      // addClientContactView({ clientId: data?.id, action: "edit_client" })
                      clientFormRef.current?.setVisible(true)
                    }}
                  />
                )}
              </Row>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {/* <Flex gap={"6px"}> */}
                  <Title className="mbe-0 text-capitalize">
                    {(() => {
                      const clientName = localisedValue({ object: data, key: "name" })
                      const shouldTruncate = clientName?.length > 25

                      return shouldTruncate ? (
                        <Tooltip title={clientName}>{clientName.substring(0, 20)}...</Tooltip>
                      ) : (
                        clientName
                      )
                    })()}
                  </Title>
                  {/* {data?.wmCategory && (
                    <Tag
                      style={{
                        "--tag-color": categoryItem[data?.wmCategory]?.textColor,
                        "--tag-bg": categoryItem[data?.wmCategory]?.color,
                      }}
                      className={"semiBold"}
                    >
                      <Flex>
                        <Icon icon={categoryItem[data?.wmCategory]?.icon} />
                        {data?.wmCategory}
                      </Flex>
                    </Tag>
                  )} */}
                {/* </Flex> */}
                <Text type="secondaryLight">
                  {joinBytItems(
                    [
                      data?.allocationActivity.createdAt,
                      t(convertToReadableString(data?.source)),
                      ...(getPrivilege("can_view_lead_source_data", userPrivileges)
                        ? [localisedValue({ object: data?.ClientSource, key: "name" })]
                        : []),
                    ],
                    <Icon icon="GoDotFill" size=".7em" color="#D6D6D6" />
                  )}
                </Text>
              </div>
              <ClientContactCard data={data} whatsAppChatRef={whatsAppChatRef} />
            </Group>
            <Divider style={{ marginBlock: "8px" }} />
            <ClientSalesDetail data={data} styles={styles} />
            {(data?.salesProfile?.data?.filter(Boolean) ?? []).length > 0 && (
              <InfoBlock
                key={data?.salesProfile?.title}
                data={data?.salesProfile?.data}
                title={localisedValue({ object: data?.salesProfile, key: "title" })}
                customComponentObj={{
                  numberComponent: data => (
                    <>{data ? <Number isStringOnly={true} value={data} isCurrency={true} /> : <>{data}</>}</>
                  ),
                }}
              />
            )}
            {(data?.mortgageProfile?.data?.filter(Boolean) ?? []).length > 0 && (
              <InfoBlock
                key={data?.mortgageProfile?.title}
                data={data?.mortgageProfile?.data}
                title={data?.mortgageProfile?.title}
                customComponentObj={{
                  numberComponent: data => <>{data ? <Number value={data} isCurrency /> : "-"}</>,
                }}
              />
            )}
            {(demographicData?.filter(Boolean) ?? []).length > 0 && (
              <InfoBlock
                key={data?.demographicProfile?.title}
                data={demographicData}
                title={data?.demographicProfile?.title}
                customComponentObj={{
                  numberComponent: data => (
                    <>{data ? <Number isStringOnly={true} value={data} isCurrency={true} /> : <>{data}</>}</>
                  ),
                }}
              />
            )}
            {(data?.affiliateInfo?.data?.filter(Boolean) ?? []).length > 0 && (
              <InfoBlock
                key={data?.affiliateInfo?.title}
                data={data?.affiliateInfo?.data}
                title={data?.affiliateInfo?.title}
              />
            )}
            {(data?.moreInfo?.data?.filter(Boolean) ?? []).length > 0 && (
              <InfoBlock key={data?.moreInfo?.title} data={data?.moreInfo?.data} title={data?.moreInfo?.title} />
            )}
          </Group>
          <ClientForm formRef={clientFormRef} client={data} />
          <WhatsAppChat
            ref={whatsAppChatRef}
            client={data}
            sharingInfo={
              data?.InternalInquiries?.flatMap(inquiry => inquiry?.InternalInquirySharing?.flatMap(s => s.shareWith)) ||
              []
            }
          />
        </>
      )}
    </Card>
  )
}
const ClientDetailInfoSkeleton = () => (
  <Group template="initial" gap="32px">
    <Group gap="16px">
      <Row align="middle" style={{ gap: "4px 8px" }}>
        <Skeleton type="avatar" />
        <Skeleton height={20} />
        <Skeleton height={20} />
      </Row>
      <Row align="middle" style={{ gap: 4 }}>
        <Skeleton height={36} width={250} />
        {/* <Skeleton type="avatar" /> */}
      </Row>
      <Row style={{ gap: 4 }}>
        {[1, 2, 3].map(e => (
          <Skeleton style={{ borderRadius: 40, height: 32 }} />
        ))}
      </Row>
      <Row className="fs14" style={{ flexFlow: "column", gap: 4 }}>
        {[1, 2].map(e => (
          <Row style={{ gap: 6 }}>
            <Skeleton type="avatar" size={20} />
            <Skeleton height={20} width={150} />
          </Row>
        ))}
      </Row>
    </Group>
    <InfoBlock skeletonLoading />
    <InfoBlock skeletonLoading />
  </Group>
)
export default ClientDetailInfo
